import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db, auth } from '../firebase';
import { collection, query, where, getDocs, writeBatch, doc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useConfirm } from '../context/ConfirmContext';

const CATEGORIES = ['הכל', 'כללי', 'איטלקי', 'קינוחים', 'דגים', 'בשר', 'בצקים', 'בישולים'];

const RecipeBookScreen = () => {
  const [recipes, setRecipes]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [searchTerm, setSearchTerm]         = useState('');
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedRecipes, setSelectedRecipes] = useState([]);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const navigate  = useNavigate();
  const location  = useLocation();
  const [user]    = useAuthState(auth);
  const { confirm } = useConfirm();

  const initialFilter = location.state?.category || 'הכל';
  const [filter, setFilter] = useState(initialFilter);

  useEffect(() => {
    const fetchRecipes = async () => {
      if (!user) { setLoading(false); return; }
      setLoading(true);
      try {
        const q   = query(collection(db, 'recipes'), where('userId', '==', user.uid));
        const snap = await getDocs(q);
        setRecipes(snap.docs.map(d => ({ ...d.data(), id: d.id })));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchRecipes();
  }, [user]);

  const handleMultiDelete = async () => {
    if (!selectedRecipes.length) return;
    const ok = await confirm(`למחוק ${selectedRecipes.length} מתכונים?`, {
      subtext: 'פעולה זו לא ניתנת לביטול.',
      confirmLabel: 'מחק הכל',
      danger: true,
    });
    if (!ok) return;
    setLoading(true);
    try {
      const batch = writeBatch(db);
      selectedRecipes.forEach(id => batch.delete(doc(db, 'recipes', id)));
      await batch.commit();
      setRecipes(prev => prev.filter(r => !selectedRecipes.includes(r.id)));
      setSelectedRecipes([]);
      setIsSelectionMode(false);
    } catch (e) { alert('שגיאה במחיקת מתכונים'); }
    finally { setLoading(false); }
  };

  const handleCardClick = (recipe) => {
    if (isSelectionMode) {
      setSelectedRecipes(prev =>
        prev.includes(recipe.id) ? prev.filter(id => id !== recipe.id) : [...prev, recipe.id]
      );
    } else {
      navigate('/detail', { state: { recipe } });
    }
  };

  const handleLongPressStart = (recipe) => {
    const t = setTimeout(() => { setIsSelectionMode(true); setSelectedRecipes([recipe.id]); }, 500);
    setLongPressTimer(t);
  };
  const handleLongPressEnd = () => { if (longPressTimer) { clearTimeout(longPressTimer); setLongPressTimer(null); } };

  const sorted = [...recipes].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    return 0;
  });

  const myRecipes = sorted
    .filter(r => !r.isShared && r.category !== 'משותף')
    .filter(r => filter === 'הכל' || r.category === filter)
    .filter(r => r.content?.toLowerCase().includes(searchTerm.toLowerCase()));

  const sharedRecipes = sorted
    .filter(r => r.isShared || r.category === 'משותף')
    .filter(r => filter === 'הכל' || r.category === filter)
    .filter(r => r.content?.toLowerCase().includes(searchTerm.toLowerCase()));

  const renderCard = (recipe) => {
    const isSelected = selectedRecipes.includes(recipe.id);
    const title = recipe.content?.split('\n')[0]?.replace('#', '').trim() || 'מתכון';

    return (
      <div
        key={recipe.id}
        onClick={() => handleCardClick(recipe)}
        onMouseDown={() => handleLongPressStart(recipe)}
        onMouseUp={handleLongPressEnd}
        onMouseLeave={handleLongPressEnd}
        onTouchStart={() => handleLongPressStart(recipe)}
        onTouchEnd={handleLongPressEnd}
        className={`bg-surface-container-lowest rounded-xl p-5 flex items-center gap-4 recipe-card-shadow cursor-pointer border-2 transition-all duration-200 hover:-translate-y-0.5 ${
          isSelected ? 'border-primary bg-primary/5' : 'border-transparent'
        }`}
      >
        {/* selection checkbox */}
        {isSelectionMode && (
          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
            isSelected ? 'border-primary bg-primary' : 'border-outline-variant'
          }`}>
            {isSelected && <span className="material-symbols-outlined text-white text-sm" style={{fontSize:'14px'}}>check</span>}
          </div>
        )}

        {/* thumbnail placeholder / image */}
        <div className="w-16 h-16 rounded-xl flex-shrink-0 bg-surface-container flex items-center justify-center overflow-hidden">
          {recipe.imageUrl
            ? <img src={recipe.imageUrl} alt={title} className="w-full h-full object-cover" />
            : <span className="material-symbols-outlined text-outline text-2xl">restaurant</span>
          }
        </div>

        {/* content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-headline font-bold text-on-surface text-base truncate mb-1">
            {recipe.isPinned && <span className="mr-1 text-sm">📌</span>}
            {title.substring(0, 40)}{title.length > 40 ? '...' : ''}
            {recipe.isFavorite && <span className="mr-1 text-sm">❤️</span>}
          </h3>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-semibold rounded-full">
              {recipe.category === 'משותף' ? 'משותף 🤝' : recipe.category}
            </span>
           
            <span className="text-xs text-outline">{recipe.date}</span>
            {recipe.rating > 0 && (
              <span className="text-xs text-yellow-500 font-bold">★ {recipe.rating}</span>
            )}
          </div>
        </div>

        <span className="material-symbols-outlined text-outline-variant text-xl flex-shrink-0">chevron_left</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
          <p className="text-on-surface-variant font-medium">טוען מתכונים...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen text-on-surface pb-32 font-body" dir="rtl">

      {/* ── Header ── */}
      <header className="sticky top-0 z-50 bg-[#f9f9f9]/80 backdrop-blur-md px-6 py-4">
        <div className="flex justify-between items-center max-w-7xl mx-auto">
          <button onClick={() => navigate('/add')} className="p-2 rounded-full hover:bg-primary/10 transition-colors" title="הוסף מתכון">
            <span className="material-symbols-outlined text-primary text-2xl">add</span>
          </button>
          <h1 className="font-headline font-bold text-xl text-primary">ספר המתכונים שלי</h1>
          <button onClick={() => navigate('/favorites')} className="p-2 rounded-full hover:bg-primary/10 transition-colors" title="מועדפים">
            <span className="material-symbols-outlined text-primary">favorite</span>
          </button>
        </div>
      </header>

      <main className="px-6 pt-6 max-w-7xl mx-auto">

        {/* ── Title ── */}
        <div className="mb-8">
          <h2 className="text-3xl font-headline font-extrabold tracking-tight mb-1">הטעמים שלך, במקום אחד</h2>
          <p className="text-on-surface-variant">
            {myRecipes.length + sharedRecipes.length} מתכונים שמורים
          </p>
        </div>

        {/* ── Search ── */}
        <div className="relative mb-8">
          <input
            type="text"
            placeholder="חפש מתכון, מרכיב או סגנון..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full h-14 bg-surface-container-lowest rounded-full px-14 text-base border-none outline-none ring-1 ring-outline-variant/20 focus:ring-4 focus:ring-primary/15 transition-all shadow-sm"
          />
          <span className="material-symbols-outlined absolute right-5 top-1/2 -translate-y-1/2 text-primary text-xl">search</span>
        </div>

        {/* ── Category Filter ── */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-8">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`whitespace-nowrap px-5 py-2.5 rounded-full font-label font-semibold text-sm transition-all active:scale-95 ${
                filter === cat
                  ? 'bg-primary text-white shadow-md shadow-primary/20'
                  : 'bg-surface-container-high text-on-surface hover:bg-surface-container-highest'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* ── Recipe Lists ── */}
        {myRecipes.length === 0 && sharedRecipes.length === 0 ? (
          <div className="py-16 flex flex-col items-center text-center border-2 border-dashed border-outline-variant/30 rounded-xl">
            <span className="material-symbols-outlined text-5xl text-outline-variant mb-4">restaurant</span>
            <p className="text-on-surface-variant font-medium mb-1">לא נמצאו מתכונים</p>
            <p className="text-sm text-outline">נסה לחפש בשם אחר או שנה את הקטגוריה</p>
          </div>
        ) : (
          <div className="space-y-4">
            {myRecipes.length > 0 && (
              <>
                {sharedRecipes.length > 0 && (
                  <h3 className="font-headline font-bold text-lg text-on-surface mb-2">המתכונים שלי</h3>
                )}
                <div className="space-y-3">
                  {myRecipes.map(renderCard)}
                </div>
              </>
            )}
            {sharedRecipes.length > 0 && (
              <>
                <h3 className="font-headline font-bold text-lg text-on-surface mt-6 mb-2">ששותפו איתי 🤝</h3>
                <div className="space-y-3">
                  {sharedRecipes.map(renderCard)}
                </div>
              </>
            )}
          </div>
        )}
      </main>

      {/* ── Selection Action Bar ── */}
      {isSelectionMode && (
        <div className="fixed bottom-24 left-4 right-4 bg-surface-container-lowest rounded-xl px-6 py-4 recipe-card-shadow flex justify-between items-center z-40">
          <button
            onClick={() => { setIsSelectionMode(false); setSelectedRecipes([]); }}
            className="font-label font-bold text-on-surface-variant"
          >
            בטל
          </button>
          <span className="font-label font-semibold text-on-surface">{selectedRecipes.length} נבחרו</span>
          <button
            onClick={handleMultiDelete}
            disabled={selectedRecipes.length === 0}
            className={`flex items-center gap-2 px-5 py-2 rounded-full font-label font-bold text-white transition-all ${
              selectedRecipes.length > 0 ? 'bg-error' : 'bg-error/40'
            }`}
          >
            <span className="material-symbols-outlined text-base">delete</span>
            מחק
          </button>
        </div>
      )}

    </div>
  );
};

export default RecipeBookScreen;
